import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Measurement } from '@kentik/ui-shared/query/query.types';
import { UdeFormProps } from './form.types';
import { CATEGORIES, getCategoryLabel, SearchResultItem } from './CategoryPanel';
import {
  BreadcrumbSegment,
  formatMeasurementDisplay,
  getCategoryPrefix,
  getMeasurementBreadcrumb
} from './categoryUtils';
import { addDimensionToForm, addMetricToForm, applyDefaultQuery } from './selectionHandlers';

interface UseMeasurementNavigationOptions {
  form: UdeFormProps['form'];
  measurements: Measurement[];
  measurementName: string | null | undefined;
  queryResetSignal?: number;
  discardSignal?: number;
}

interface UseMeasurementNavigationResult {
  categoryPrefix: string | null;
  breadcrumbProps: { categoryLabel: string; segments: BreadcrumbSegment[] };
  resetQueryFields: () => void;
  applyCategory: (prefix: string) => void;
  applyMeasurement: (measurement: string) => void;
  handleSearchSelect: (item: SearchResultItem) => void;
}

export function useMeasurementNavigation({
  form,
  measurements,
  measurementName,
  queryResetSignal,
  discardSignal
}: UseMeasurementNavigationOptions): UseMeasurementNavigationResult {
  // Tracked separately because a category can be picked before a full measurement
  const [categoryPrefix, setCategoryPrefix] = useState<string | null>(() =>
    measurementName ? getCategoryPrefix(measurementName) : null
  );

  // Sync when measurement changes externally (e.g. empty-state AI input); the inequality guard prevents loops
  useEffect(() => {
    if (!measurementName) {
      return;
    }
    const nextPrefix = getCategoryPrefix(measurementName);
    if (nextPrefix && nextPrefix !== categoryPrefix) {
      setCategoryPrefix(nextPrefix);
    }
  }, [measurementName, categoryPrefix]);

  // Parent already cleared the form on reset, so only local UI state is reset here
  const lastResetSignal = useRef(queryResetSignal);
  useEffect(() => {
    if (lastResetSignal.current === queryResetSignal) {
      return;
    }
    lastResetSignal.current = queryResetSignal;
    setCategoryPrefix(null);
  }, [queryResetSignal]);

  // Discard restores the saved query, so realign the category with the restored measurement
  const lastDiscardSignal = useRef(discardSignal);
  useEffect(() => {
    if (lastDiscardSignal.current === discardSignal) {
      return;
    }
    lastDiscardSignal.current = discardSignal;
    setCategoryPrefix(getCategoryPrefix(measurementName || ''));
  }, [discardSignal, measurementName]);

  const resetQueryFields = useCallback(() => {
    form.fieldStates.forEach((field) => {
      if (
        /^config\.data\.(dimensions|filters|measurement|metrics|reAgg|rollups|sort|window)/.test(field.originalName)
      ) {
        field.setToDefault({ validate: false });
      }
    });

    form.setValue('config.chart.yAxis.metric', '');
    form.setValue('config.chart.yAxis.aggregation', '');
    form.setValue('config.chart.yAxis.multipleAxis', false);
  }, [form]);

  const applyCategory = useCallback(
    (prefix: string) => {
      resetQueryFields();
      setCategoryPrefix(prefix);

      const matchingMeasurement = measurements.find((m) => m.measurement === prefix);
      if (matchingMeasurement) {
        form.setValue('config.data.measurement', prefix);
        applyDefaultQuery(form, prefix, measurements);
      }
    },
    [resetQueryFields, form, measurements]
  );

  const applyMeasurement = useCallback(
    (measurement: string) => {
      resetQueryFields();
      const prefix = getCategoryPrefix(measurement);
      if (prefix) {
        setCategoryPrefix(prefix);
      }
      form.setValue('config.data.measurement', measurement);
      if (measurement !== '/traffic') {
        applyDefaultQuery(form, measurement, measurements);
      }
    },
    [resetQueryFields, form, measurements]
  );

  const handleSearchSelect = useCallback(
    (item: SearchResultItem) => {
      resetQueryFields();
      setCategoryPrefix(item.categoryPrefix);
      form.setValue('config.data.measurement', item.measurement);

      if (item.type === 'metric') {
        addMetricToForm(form, item.value, measurements);
      } else if (item.type === 'dimension') {
        addDimensionToForm(form, item.value);
      }

      applyDefaultQuery(form, item.measurement, measurements);
    },
    [form, measurements, resetQueryFields]
  );

  const measurementDisplayLabel = useMemo(() => {
    if (!categoryPrefix) {
      return '';
    }
    // Prefer the measurement's prefix: categoryPrefix may lag one render behind an external measurement change
    const topLevel = (measurementName && getCategoryPrefix(measurementName)) || getCategoryPrefix(categoryPrefix);
    if (measurementName && topLevel) {
      return formatMeasurementDisplay({
        measurement: measurementName,
        prefix: topLevel,
        categoryLabel: getCategoryLabel(topLevel)
      });
    }
    if (topLevel && categoryPrefix !== topLevel) {
      // Sub-category selected but no measurement yet: show "NMS / Interfaces"
      return formatMeasurementDisplay({
        measurement: categoryPrefix,
        prefix: topLevel,
        categoryLabel: getCategoryLabel(topLevel)
      });
    }
    return getCategoryLabel(categoryPrefix);
  }, [measurementName, categoryPrefix]);

  const measurementBreadcrumb = useMemo(() => {
    if (!measurementName || !categoryPrefix) {
      return null;
    }
    const topLevel = getCategoryPrefix(measurementName) ?? getCategoryPrefix(categoryPrefix);
    if (!topLevel) {
      return null;
    }
    const category = CATEGORIES.find((c) => c.key === topLevel);
    return getMeasurementBreadcrumb({
      measurements,
      categoryPrefix: topLevel,
      measurement: measurementName,
      categoryLabel: getCategoryLabel(topLevel),
      hideChildren: category?.hideChildren
    });
  }, [measurements, measurementName, categoryPrefix]);

  // Without selectable siblings, fall back to the full formatted path as a static label
  const breadcrumbProps = useMemo(() => {
    if (measurementBreadcrumb && measurementBreadcrumb.segments.length > 0) {
      return { categoryLabel: measurementBreadcrumb.categoryLabel, segments: measurementBreadcrumb.segments };
    }
    return { categoryLabel: measurementDisplayLabel, segments: [] };
  }, [measurementBreadcrumb, measurementDisplayLabel]);

  return {
    categoryPrefix,
    breadcrumbProps,
    resetQueryFields,
    applyCategory,
    applyMeasurement,
    handleSearchSelect
  };
}
